import { createFileRoute } from '@tanstack/react-router';
import { RequirePermission } from '../../../features/layout/RequirePermission';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { userSchema } from '@itvisits/shared';
import { ClustersPage } from '../../../features/admin/clusters/ClustersPage';

/** Cluster leaders come from the full users list (clusters.manage implies users.manage for Super Admin). */
function useLeaders() {
  return useQuery({
    queryKey: ['users', 'leaders'],
    queryFn: async () => {
      const res = await fetch('/api/v1/users?pageSize=100&page=1', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Could not load users');
      const body = z.object({ data: z.array(userSchema) }).parse(await res.json());
      return body.data.filter((u) => u.role === 'Cluster Leader' && u.isActive);
    },
  });
}

const searchSchema = z.object({
  q: z.string().catch(''),
  leader: z.enum(['all', 'with', 'without']).catch('all'),
});

export const Route = createFileRoute('/_authed/admin/clusters')({
  validateSearch: searchSchema,
  component: Guarded,
});

function ClustersRoute(): React.JSX.Element {
  const leaders = useLeaders();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <ClustersPage
      leaders={(leaders.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
      search={search}
      onSearch={(next) => void navigate({ search: next, replace: true })}
    />
  );
}

function Guarded(): React.JSX.Element {
  return (
    <RequirePermission permission="clusters.manage">
      <ClustersRoute />
    </RequirePermission>
  );
}
